import { useMemo } from "react";
import { wordDiff } from "../anchor/diff";
import type { EditArtifact } from "../chat/artifacts";
import type { CardStatus } from "../chat/controller";
import type { TrackingMode } from "../hands/types";
import { CardStatusLine, PendingWarning } from "./cardParts";

export interface EditCardProps {
  edit: EditArtifact;
  status: CardStatus;
  /** Put the why in the Word comment (default off: the comment only says "Suggested by Ella", spec D4). */
  includeReason: boolean;
  /** Current Track Changes mode; `null` while unknown. "Apply clean" is offered only when it is "Off". */
  trackingMode: TrackingMode | null;
  /** Authors of pending tracked changes in this paragraph (for the server's pending-change warning). */
  pendingAuthors?: readonly string[];
  /** True while "Apply all" runs: the card's own buttons wait. */
  locked?: boolean;
  onApply: (mode: "tracked" | "clean") => void;
  onDismiss: () => void;
  onRestore?: () => void;
  onToggleReason: (value: boolean) => void;
  onJump?: () => void;
  /** The edit is applied but its Word comment is not: offer to add just the comment. */
  commentRetry?: { busy: boolean; onRetry: () => void } | null;
}

const OP_LABEL: Record<EditArtifact["op"], string> = { replace: "Edit", delete: "Delete", insert_after: "Add after" };

type Segment = { kind: "equal" | "delete" | "insert"; text: string };

export function editSegments(edit: Pick<EditArtifact, "op" | "replacement">, before: string): Segment[] {
  if (edit.op === "delete") return [{ kind: "delete", text: before }];
  if (edit.op === "insert_after") return [{ kind: "equal", text: before }, { kind: "insert", text: edit.replacement }];
  return (
    wordDiff(before, edit.replacement)?.segments ?? [
      { kind: "delete", text: before },
      { kind: "insert", text: edit.replacement },
    ]
  );
}

export function DiffText({ segments }: { segments: readonly Segment[] }) {
  return (
    <p className="doc-text diff" data-testid="edit-diff">
      {segments.map((s, i) =>
        s.kind === "delete" ? <del key={i}>{s.text}</del> : s.kind === "insert" ? <ins key={i}>{s.text}</ins> : <span key={i}>{s.text}</span>,
      )}
    </p>
  );
}

/**
 * One verified edit: before -> after word diff, the why (kept in the pane), and Apply / Dismiss. Every
 * string from the document or the model is rendered as a React text node, never as HTML.
 */
export function EditCard({
  edit,
  status,
  includeReason,
  trackingMode,
  pendingAuthors = [],
  locked = false,
  onApply,
  onDismiss,
  onRestore,
  onToggleReason,
  onJump,
  commentRetry = null,
}: EditCardProps) {
  const before = edit.matched_text || edit.quote;
  const segments = useMemo(() => editSegments(edit, before), [edit, before]);
  const working = status.state === "working";
  const done = status.state === "done";
  const busy = working || locked;

  if (status.state === "dismissed") {
    return (
      <article className="xcard xcard--dismissed" aria-label="Dismissed edit">
        <span className="xcard__dismissed-text">Edit dismissed</span>
        {onRestore ? (
          <button type="button" className="link-btn" onClick={onRestore}>
            Undo
          </button>
        ) : null}
      </article>
    );
  }

  return (
    <article className={`xcard xcard--edit${done ? " is-done" : ""}`} aria-label="Suggested edit">
      <header className="xcard__head">
        <span className="xcard__kind">{OP_LABEL[edit.op]}</span>
        {onJump ? (
          <button type="button" className="link-btn" onClick={onJump}>
            Show
          </button>
        ) : null}
      </header>
      <DiffText segments={segments} />
      {edit.why ? (
        <p className="xcard__why">
          <span className="xcard__label">Why</span>
          {edit.why}
        </p>
      ) : null}
      {edit.warnings.includes("pending_change_warning") && !done ? <PendingWarning authors={pendingAuthors} /> : null}
      {!done ? (
        <>
          <label className="check">
            <input type="checkbox" checked={includeReason} onChange={(e) => onToggleReason(e.target.checked)} disabled={busy} />
            <span>
              Include the reason in the Word comment
              <span className="check__hint">{includeReason ? "The other side will see it." : "Otherwise it only says “Suggested by Ella”."}</span>
            </span>
          </label>
          <div className="xcard__actions">
            <button type="button" className="btn btn--primary btn--sm" disabled={busy} onClick={() => onApply("tracked")}>
              {working ? "Applying…" : "Apply as tracked change"}
            </button>
            {trackingMode === "Off" ? (
              <button type="button" className="btn btn--sm" disabled={busy} title="Track Changes is off in this document" onClick={() => onApply("clean")}>
                Apply clean
              </button>
            ) : null}
            <button type="button" className="btn btn--quiet btn--sm" disabled={busy} onClick={onDismiss}>
              Dismiss
            </button>
          </div>
        </>
      ) : null}
      <CardStatusLine status={status} />
      {done && commentRetry ? (
        <div className="xcard__actions">
          <button type="button" className="btn btn--sm" disabled={commentRetry.busy || locked} onClick={commentRetry.onRetry}>
            {commentRetry.busy ? "Adding comment…" : "Retry comment"}
          </button>
        </div>
      ) : null}
    </article>
  );
}
